import { PropsWithChildren } from "react";

import Heading from "components/heading";
import HeroSection from "components/hero-section";
import { sendAnalyticEvent } from "components/layout/page/use-page";
import Image from "next/image";
import Link from "next/link";
import FigureImage from "public/images/enerflo-ohm-analytics-case-study.png";

const title = "Sales Dealers, Installers (EPC) and Hybrid Orgs rely on multiple tools from Lead to Install";

type BlockSectionProps = PropsWithChildren<{ title: string; description: string }>;
const BlockSection = ({ title, description, children }: BlockSectionProps) => (
  <div className="flex-1 p-4 md:p-6 bg-gray-100 space-y-4 text-center rounded-lg shadow">
    <Heading tag="h5" className="text-primary">
      {title}
    </Heading>
    <p>{description}</p>
    {children}
  </div>
);

const RelyOnMultipleTools = () => {
  return (
    <div className="bg-white">
      <HeroSection title={title}>
        <div className="flex flex-col items-center">
          <HeroSection.Content className="w-full xl:w-4/5 md:text-center">
            <HeroSection.Header>
              <Heading tag="h2">{title}</Heading>
            </HeroSection.Header>
            <HeroSection.Description>
              Enerflo combines them with native platform features into one cohesive solar sales and install platform
            </HeroSection.Description>
          </HeroSection.Content>

          <div className="flex flex-col gap-4 md:flex-row md:gap-8 mt-8 px-6 md:mt-14 xl:w-4/5 xl:gap-14">
            <BlockSection
              title="Your Enerflo, Your Way"
              description="As the ONLY open API, lead-to-PTO solar platform with the most integrations, Enerflo is designed to put the power in the hands of our partners. Unlock your productivity with additional integrations for:"
            >
              <div className="inline-block mx-auto md:block">
                <ul className="list-disc list-outside ml-4 space-y-2 text-left xs:grid xs:grid-cols-2 xs:gap-2 xs:space-y-0 md:block md:space-y-2">
                  <li>CRM</li>
                  <li>Productivity Tools</li>
                  <li>Dialers & SMS</li>
                  <li>Welcome Calls & Third Party Verification (TPV)</li>
                  <li>Storage & Batteries</li>
                  <li>SmartHome</li>
                  <li>Reporting</li>
                  <li>And More</li>
                </ul>
              </div>
            </BlockSection>
            <BlockSection
              title="Enerflo Drives Efficiency"
              description="An Ohm Analytics Case Study revealed that Enerflo Partners operate more efficiently, reducing their install timelines by 62%, while nearly doubling their volume. Yep, you read that right."
            >
              <div className="sm:w-3/5 md:w-full lg:w-4/5 mx-auto">
                <figure className="p-3 flex-grow w-full">
                  <Image
                    src={FigureImage}
                    alt="Ohm Analytics: Enerflo Case Study - Reduce install timelines by 62%."
                    layout="responsive"
                    quality={100}
                    placeholder="blur"
                    className="crispImage"
                    sizes="(min-width: 768px) 60vw, 100vw"
                    lazyBoundary="400px"
                  />
                </figure>
                <Link href="/resources/ohm-analytics-white-paper-install-timelines-down-volume-almost-doubled">
                  <a
                    className="btn btn-primary w-full"
                    onClick={() => sendAnalyticEvent("Engagement Button", "Buttons", "View Case Study")}
                  >
                    View the Case Study
                  </a>
                </Link>
              </div>
            </BlockSection>
          </div>
        </div>
      </HeroSection>
    </div>
  );
};

export default RelyOnMultipleTools;
